/**
 * Contratto API v1 (prefisso /api/v1). Gli schemi zod validano gli input lato server;
 * i tipi *Dto descrivono le risposte JSON. Date: "YYYY-MM-DD"; timestamp: ISO 8601.
 * Errori: { error: { code: string, message: string, details?: unknown } } con status HTTP coerente
 * (400 validazione, 401 non autenticato, 403 permesso, 404, 409 conflitto di stato/regola).
 */
import { z } from "zod";
import {
  ACT_FLOW,
  ANS,
  CODE_YEAR_MODES,
  ZERO_ACTIONS,
  SUPPLIER_RATING_RULES,
  AUDIT_FLOW,
  AUDIT_ORIGINS,
  AUDIT_TYPES,
  CAP_STATUSES,
  EVIDENCE_STATUSES,
  EVIDENCE_TARGETS,
  NC_SEVERITIES,
  PRIORITIES,
  RISKS,
  ROLES,
  SECTIONS,
  type AppConfig,
  type MonitoringOff,
  type RatingOverride,
  type Scores,
} from "./domain.js";
import { ACTIVE_CHANNELS, NOTIFICATION_KINDS, RECIPIENT_ROLES, type NotificationKind, type NotificationsConfig } from "./notifications.js";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
const id = z.string().min(1);
const optText = z.string().max(5000).optional().default("");
/** Booleano da querystring: "true"/"1" = true, "false"/"0" = false (z.coerce.boolean darebbe true per "false"). */
const qBool = z.preprocess((v) => (typeof v === "string" ? v === "true" || v === "1" : v), z.boolean());

/* ---------------- common ---------------- */
export interface ErrorDto {
  error: { code: string; message: string; details?: unknown };
}
export interface Page<T> {
  items: T[];
  total: number;
}
export interface PersonRef {
  id: string;
  name: string;
}

/* ---------------- auth ---------------- */
export const LoginBody = z.object({ email: z.string().email(), password: z.string().min(1) });
/** Regole password: vedi password.ts; le applica il server (errore 400 code "weak_password"). */
export const AcceptInviteBody = z.object({ token: z.string().min(10), password: z.string().min(1).max(200), name: z.string().min(1).optional() });
export const ChangePasswordBody = z.object({ current: z.string().min(1), password: z.string().min(1).max(200) });
export const ForgotPasswordBody = z.object({ email: z.string().email() });
export const ResetPasswordBody = z.object({ token: z.string().min(10), password: z.string().min(1).max(200) });
export const LoginTotpBody = z.object({ mfaToken: z.string().min(10), code: z.string().min(6).max(32) });
export const TotpEnableBody = z.object({ code: z.string().min(6).max(8) });
export const TotpDisableBody = z.object({ password: z.string().min(1) });
export const RecoveryRegenBody = z.object({ password: z.string().min(1), code: z.string().min(6).max(32) });
export interface MeDto {
  id: string;
  email: string;
  name: string;
  role: (typeof ROLES)[number];
  supplierId: string | null;
  lang: "it" | "en";
  /** Sicurezza account: setupNeeded = ruolo con TOTP obbligatorio non ancora configurato (sessione limitata a /security). */
  mfa: { enabled: boolean; required: boolean; setupNeeded: boolean };
}
/** Risposta di POST /auth/login: sessione completa (MeDto) oppure primo passo del login a due fattori. */
export interface MfaRequiredDto {
  mfaRequired: true;
  mfaToken: string;
}
export interface SecurityDto {
  totp: { enabled: boolean; pending: boolean; required: boolean; canDisable: boolean };
  recoveryCodesLeft: number;
}
export interface TotpSetupDto {
  secret: string;
  otpauthUri: string;
  issuer: string;
  account: string;
}
export interface RecoveryCodesDto {
  recoveryCodes: string[];
}

/* ---------------- users (admin) ---------------- */
export const UserInviteBody = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: z.enum(ROLES),
  supplierId: id.nullable().optional(),
});
export const UserPatchBody = z.object({
  name: z.string().min(1).optional(),
  role: z.enum(ROLES).optional(),
  supplierId: id.nullable().optional(),
  disabled: z.boolean().optional(),
});
export interface UserDto {
  id: string;
  email: string;
  name: string;
  role: (typeof ROLES)[number];
  supplierId: string | null;
  status: "invited" | "active" | "disabled";
  lastLoginAt: string | null;
  createdAt: string;
  totpEnabled: boolean;
}
/** Invito massivo dei referenti fornitori (admin). dryRun=true (default) = solo anteprima. */
export const InviteBulkBody = z.object({
  supplierIds: z.array(id).max(2000).optional(),
  dryRun: z.boolean().default(true),
});
export interface InviteBulkRowDto {
  supplierId: string;
  supplierName: string;
  email: string;
  name: string;
}
export interface InviteBulkDto {
  dryRun: boolean;
  /** contatti che diventerebbero (o sono diventati) utenti */
  newUsers: InviteBulkRowDto[];
  /** contatti già utenti, o email ripetuta nella stessa lista */
  existing: (InviteBulkRowDto & { reason: "user_exists" | "duplicate_in_list" })[];
  /** fornitori senza email di contatto (o email non valida) */
  noEmail: { supplierId: string; supplierName: string; email: string }[];
  /** solo dryRun=false: inviti creati con il link (consegnabile a mano se la posta non è attiva) */
  created: (InviteBulkRowDto & { userId: string; inviteUrl: string })[];
}
/** Risposta di invito/reinvito: il link si consegna a mano in v1 (nessun invio mail). */
export interface InviteDto {
  user: UserDto;
  inviteUrl: string;
  expiresAt: string;
}

/* ---------------- suppliers ---------------- */
export const SupplierBody = z.object({
  code: z.string().min(1), // codice SAP
  name: z.string().min(1),
  vat: z.string().optional().default(""),
  country: z.string().optional().default(""),
  legalAddress: z.string().optional().default(""),
  contactName: z.string().optional().default(""),
  contactEmail: z.string().optional().default(""),
  contactPhone: z.string().optional().default(""),
  category: z.string().optional().default(""),
  sector: z.string().optional().default(""),
  risk: z.enum(RISKS).default("medium"),
});
export const SupplierQuery = z.object({
  q: z.string().optional(),
  risk: z.enum(RISKS).optional(),
  category: z.string().optional(),
  phaseOut: qBool.optional(),
  /** solo admin: anche i fornitori disattivati (RAT26-F3.1b); quelli in phase-out si vedono sempre (AUD-F8.4) */
  includeInactive: qBool.optional(),
});
export interface SupplierDto {
  id: string;
  vendorhubId: string | null;
  code: string;
  name: string;
  vat: string;
  country: string;
  legalAddress: string;
  displayCode: string;
  nameLocal: string;
  vendorStatus: string;
  /** riga vendor del VendorHub (campi non vuoti, senza coordinate bancarie) */
  vendorhubData: Record<string, string> | null;
  businessUnits: string[];
  brands: string[];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  category: string;
  sector: string;
  risk: (typeof RISKS)[number];
  /** classe di rischio impostata a mano dall'admin, con la motivazione (AUD-F2.20) */
  riskManual: boolean;
  riskNote: string | null;
  phaseOut: boolean;
  phaseOutAt: string | null;
  phaseOutReason: string | null;
  phaseOutAuditId: string | null;
  rating: number | null; // score totale dell'ultimo audit chiuso
  /** "D" se l'ultimo audit chiuso ha l'override delle regole del rating (la classe è D qualunque sia lo score) */
  ratingOverride: RatingOverride | null;
  ratingAt: string | null;
  /** rating impostato a mano dall'admin, con la motivazione (AUD-F2.20) */
  ratingManual: boolean;
  ratingNote: string | null;
  /** RAT26-F3.1b: disattivato = fuori da elenchi, ricerche, dashboard e copertura (solo Audit Plan, il VendorHub non cambia) */
  active: boolean;
  activeNote: string | null;
  activeAt: string | null;
  source: string;
  createdAt: string;
  /** Calcolati nella lista: */
  openNcs?: number;
  capPct?: number | null;
  lastAuditDate?: string | null;
  visitsPlannedThisYear?: number;
  visitsRequired?: number;
  factoryCount?: number;
  /** stato VendorHub peggiore fra gli stabilimenti (worstFactoryStatus) e quanti stabilimenti per stato */
  factoryStatus?: string | null;
  factoryStatuses?: Record<string, number>;
}

/* ---------------- stabilimenti (AUD-F2.15) ---------------- */
export const FactoryBody = z.object({
  supplierId: id, // fornitore a cui si lega lo stabilimento
  code: z.string().trim().max(50).optional().default(""), // codice SAP, facoltativo
  name: z.string().trim().min(1).max(255),
  nameLocal: z.string().max(255).optional().default(""),
  address: z.string().max(2000).optional().default(""),
  city: z.string().max(120).optional().default(""),
  country: z.string().max(120).optional().default(""),
  tier: z.union([z.literal(2), z.literal(3)]).optional().default(2),
});
export const FactoryPatch = FactoryBody.omit({ supplierId: true }).partial();
export const FactoryLinkBody = z.object({ supplierId: id, relationshipType: z.string().max(50).optional().default("") });

/* ---------------- valori a mano dell'admin (AUD-F2.20) ---------------- */
/** Rating a mano: score 0-100 (la classe A-D si ricava come per gli audit); null = torna al calcolo dagli audit. */
export const ManualRatingBody = z.object({ rating: z.number().min(0).max(100).nullable(), reason: z.string().trim().min(3).max(1000) });
export const ManualRiskBody = z.object({ risk: z.enum(RISKS), reason: z.string().trim().min(3).max(1000) });
/** Attiva o disattiva fornitore o stabilimento (solo admin, RAT26-F3.1b): motivazione obbligatoria, finisce nel registro. */
export const ActiveBody = z.object({ active: z.boolean(), reason: z.string().trim().min(3).max(1000) });
/**
 * Phase-out del fornitore a mano (solo admin, AUD-F8.4): per un fornitore chiuso o da non usare più, senza un audit zero
 * tolerance. Motivazione obbligatoria, finisce nel registro; data di default oggi. Toglierlo vale solo per quello a mano.
 */
export const SupplierPhaseOutBody = z.object({ phaseOut: z.boolean(), reason: z.string().trim().min(3).max(1000), date: isoDate.optional() });
export const FactoryQuery = z.object({ q: z.string().optional(), supplierId: id.optional(), includeInactive: qBool.optional() });
export interface FactorySupplierRef {
  id: string;
  code: string;
  name: string;
  relationshipType: string;
  isSeat: boolean;
}
export interface FactoryDto {
  id: string;
  vendorhubId: string | null;
  code: string | null;
  displayCode: string;
  name: string;
  nameLocal: string;
  address: string;
  city: string;
  country: string;
  tier: number;
  grade: string;
  status: string;
  businessUnits: string[];
  brands: string[];
  /** riga factory del VendorHub (campi non vuoti) */
  vendorhubData: Record<string, string> | null;
  isSeat: boolean;
  rating: number | null;
  ratingOverride: RatingOverride | null;
  ratingAt: string | null;
  ratingManual: boolean;
  ratingNote: string | null;
  active: boolean;
  activeNote: string | null;
  activeAt: string | null;
  source: string;
  suppliers: FactorySupplierRef[];
  /** Calcolati: */
  auditCount?: number;
  lastAuditDate?: string | null;
  nextAuditDate?: string | null;
}
export interface ContactDto {
  id: string;
  supplierId: string | null;
  factoryId: string | null;
  name: string;
  email: string;
  phone: string;
  title: string;
  role: string;
  isPrimary: boolean;
}
/** Scheda «Report» dello stabilimento (AUD-F2.16): per audit, in ordine di data, il PDF generato dal tool e i documenti caricati. */
export interface FactoryReportDoc {
  evidenceId: string;
  v: number;
  name: string;
  /** prima riga della descrizione: ruolo del documento (rapporto, CAP, esito, ...) */
  role: string;
  contentType: string;
  size: number;
}
/** Evidenza con il contesto per il breadcrumb (GET /evidence/:id). */
export interface EvidenceDetailDto extends EvidenceDto {
  supplierName: string;
  audit: { id: string; code: string; factoryId: string; factoryName: string; historical: boolean } | null;
}
export type AuditOutcome = "phaseout" | "pass" | "fail";
export const AUDIT_OUTCOMES: readonly AuditOutcome[] = ["phaseout", "pass", "fail"];

export interface FactoryReportEntry {
  auditId: string;
  code: string;
  date: string;
  status: string;
  historical: boolean;
  origin: string;
  client: string;
  supplierName: string;
  /** conformità alla checklist (0-100) calcolata dal tool; per gli storici lo score totale del registro, se c'è */
  conformity: number | null;
  outcome: AuditOutcome | null;
  ratingOverride: RatingOverride | null;
  /** true = c'è il report PDF generato dal tool (GET /audits/:id/report.pdf) */
  generatedReport: boolean;
  evidenceCount: number;
  documents: FactoryReportDoc[];
}
export interface FactoryDetailDto extends FactoryDto {
  audits: AuditDto[];
  contacts: ContactDto[];
  scoreHistory: { auditId: string; code: string; date: string; total: number | null }[];
}

/* ---------------- trascodifica VendorHub (AUD-F5.4) ---------------- */
/** Payload normalizzato dal dump Postgres del VendorHub: lookup (paese, business unit, brand) già risolti. Id = id del VendorHub. */
const vhStr = z.string().max(4000).default("");
export const VendorhubPayload = z.object({
  source: z.string().max(200), // nome del dump
  vendors: z.array(
    z.object({ id: z.string().min(1), code: z.string().nullable(), name: z.string().min(1), nameLocal: vhStr, displayCode: vhStr, vat: vhStr, address: vhStr, country: vhStr, status: vhStr, raw: z.record(z.string(), z.string().nullable()).default({}) }),
  ),
  factories: z.array(
    z.object({
      id: z.string().min(1),
      code: z.string().nullable(),
      name: z.string().min(1),
      nameLocal: vhStr,
      displayCode: vhStr,
      address: vhStr,
      city: vhStr,
      country: vhStr,
      tier: z.number().int(),
      grade: vhStr,
      status: vhStr,
      businessUnits: z.array(z.string()).default([]),
      brands: z.array(z.string()).default([]),
      raw: z.record(z.string(), z.string().nullable()).default({}),
    }),
  ),
  links: z.array(z.object({ id: z.string().min(1), vendorId: z.string().min(1), factoryId: z.string().min(1), relationshipType: vhStr, deleted: z.boolean().default(false) })),
  contacts: z.array(
    z.object({ id: z.string().min(1), vendorId: z.string().nullable(), factoryId: z.string().nullable(), name: z.string().min(1), email: vhStr, phone: vhStr, title: vhStr, role: vhStr, isPrimary: z.boolean().default(false) }),
  ),
});
export type VendorhubPayloadT = z.infer<typeof VendorhubPayload>;
export interface VendorhubImportReport {
  source: string;
  dryRun: boolean;
  suppliers: { created: number; updated: number; unchanged: number; codeless: string[]; missingFromDump: number };
  factories: { created: number; updated: number; unchanged: number; adoptedSeats: number; codeConflicts: string[] };
  links: { created: number; updated: number; skipped: number };
  contacts: { created: number; updated: number; unchanged: number };
  seats: { fromCode: number; single: number; created: number; existing: number; none: number };
}

/* ---------------- checklist ---------------- */
export const QuestionBody = z.object({
  section: z.enum(SECTIONS),
  number: z.string().min(1),
  area: z.string().optional().default(""),
  it: z.string().min(1),
  en: z.string().optional().default(""),
  points: z.number().int().min(0).max(50).default(10),
  order: z.number().int().default(0),
  active: z.boolean().default(true),
  /** zero tolerance: una NC su questa domanda attiva la regola onZeroTolerance della configurazione */
  zeroTolerance: z.boolean().default(false),
});
export interface ChecklistVersionDto {
  id: string;
  code: string; // "rev0-2025-09-01"
  title: string;
  active: boolean;
  questionCount: number;
  usedByAudits: number;
}
export interface QuestionDto {
  id: string;
  versionId: string;
  section: (typeof SECTIONS)[number];
  area: string;
  number: string;
  code: string; // "S 1.1", "X A.1"
  it: string;
  en: string;
  points: number;
  order: number;
  active: boolean;
  zeroTolerance: boolean;
}

/* ---------------- audits ---------------- */
export const AuditCreateBody = z.object({
  supplierId: id,
  /** stabilimento auditato (legato al fornitore); se assente, la sede del fornitore */
  factoryId: id.nullable().optional(),
  type: z.enum(AUDIT_TYPES).default("periodic"),
  plannedDate: isoDate,
  /** fine della finestra, obbligatoria per i semi-annunciati (AUD-F8.4) */
  plannedDateTo: isoDate.nullable().optional(),
  managerId: id,
  auditorId: id,
  reviewerId: id.nullable().optional(),
  ente: z.string().optional().default("Brandart - Audit interno"),
  participants: optText,
  reportTo: optText,
  scope: optText,
  parentAuditId: id.nullable().optional(), // follow-up
  origin: z.enum(AUDIT_ORIGINS).default("program"),
  client: z.string().max(120).optional().default(""), // obbligatorio se origin = client
});
/** Modifica: solo i campi inviati (niente default, altrimenti un PATCH parziale azzererebbe gli altri). */
export const AuditPatchBody = z.object({
  type: z.enum(AUDIT_TYPES).optional(),
  factoryId: id.optional(),
  plannedDate: isoDate.optional(),
  plannedDateTo: isoDate.nullable().optional(),
  managerId: id.optional(),
  auditorId: id.optional(),
  reviewerId: id.nullable().optional(),
  ente: z.string().optional(),
  participants: z.string().max(5000).optional(),
  reportTo: z.string().max(5000).optional(),
  scope: z.string().max(5000).optional(),
  origin: z.enum(AUDIT_ORIGINS).optional(),
  client: z.string().max(120).optional(),
});
export const AuditQuery = z.object({
  q: z.string().optional(),
  status: z.enum(AUDIT_FLOW).optional(),
  type: z.enum(AUDIT_TYPES).optional(),
  supplierId: id.optional(),
  factoryId: id.optional(),
  auditorId: id.optional(),
  year: z.coerce.number().int().optional(),
  origin: z.enum(AUDIT_ORIGINS).optional(),
  client: z.string().optional(),
});
export const AuditTransitionBody = z.object({ to: z.enum(AUDIT_FLOW), note: z.string().optional() });
/** Risposta a una domanda valutata. comment obbligatorio per NC_* e OSS. ownerId = responsabile della NC. */
export const AnswerBody = z.object({
  r: z.enum(ANS),
  comment: z.string().optional().default(""),
  ownerId: id.optional(),
});
/** Informazioni aggiuntive (sezione X): v = "Y" | "N", text libero. */
export const ExtraAnswerBody = z.object({ v: z.enum(["Y", "N"]), text: z.string().optional().default("") });
export const ObservationBody = z.object({ text: z.string().min(1), section: z.enum(SECTIONS).nullable().optional() });
export const PhaseoutBody = z.object({ reason: z.string().min(1), questionId: id.nullable().optional() });
export const CapStatusBody = z.object({ to: z.enum(CAP_STATUSES) });

export interface AnswerDto {
  questionId: string;
  r: (typeof ANS)[number];
  comment: string;
  byId: string | null;
  ts: string;
  editedInValidation: boolean;
  prevR: string | null;
}
export interface ExtraAnswerDto {
  questionId: string;
  v: "Y" | "N";
  text: string;
  byId: string | null;
  ts: string;
}
export interface ObservationDto {
  id: string;
  questionId: string | null;
  section: string | null;
  text: string;
  byId: string | null;
  createdAt: string;
}
export interface HistoryEntry {
  ts: string;
  by: string | null; // user id o "system"
  from?: string;
  to?: string;
  note?: string;
  progress?: number;
}
export interface AuditDto {
  id: string;
  code: string;
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  factoryId: string;
  factoryName: string;
  factoryCode: string | null;
  factoryCountry: string;
  checklistVersionId: string;
  type: (typeof AUDIT_TYPES)[number];
  status: (typeof AUDIT_FLOW)[number];
  /** phase-out (zero tolerance) oppure, per gli storici senza punteggio, esito positivo/negativo dal registro (RAT26) */
  outcome: AuditOutcome | null;
  /** null per il fornitore se l'audit è non annunciato e non ancora avviato (in quel caso non lo vede proprio). */
  plannedDate: string | null;
  /** fine della finestra dei semi-annunciati (AUD-F8.4); plannedDate ne è l'inizio */
  plannedDateTo: string | null;
  actualDate: string | null;
  managerId: string;
  auditorId: string;
  reviewerId: string | null;
  ente: string;
  participants: string;
  reportTo: string;
  scope: string;
  parentAuditId: string | null;
  scores: Scores | null;
  /** Regole del rating: "D" quando una NC critical / zero tolerance forza il rating a D (scores.total resta lo score calcolato). */
  ratingOverride: RatingOverride | null;
  ratingReason: string | null;
  capStatus: (typeof CAP_STATUSES)[number] | null;
  phaseoutReason: string | null;
  phaseoutQuestionId: string | null;
  sameScoreOfId: string | null;
  origin: (typeof AUDIT_ORIGINS)[number];
  client: string;
  historical: boolean;
  startedAt: string | null;
  completedAt: string | null;
  closedAt: string | null;
  validatedById: string | null;
  reportPublished: boolean;
  createdAt: string;
  /** Solo in lista: */
  ncCount?: number;
  openNcCount?: number;
}
export interface AuditDetailDto extends AuditDto {
  history: HistoryEntry[];
  capHistory: { ts: string; by: string | null; to: string; openNc: number }[];
  answers: Record<string, AnswerDto>; // per questionId
  extra: Record<string, ExtraAnswerDto>;
  observations: ObservationDto[];
  ncs: NcDto[];
  actions: ActionDto[];
  evidence: EvidenceDto[];
  /** Score calcolato al volo sulle risposte attuali (preliminare se l'audit non è chiuso). */
  liveScores: Scores;
  /** Override del rating sulle risposte attuali (regole di configurazione). */
  liveRating: { ratingOverride: "D" | null; ratingReason: string | null };
  /** Esito proposto dalle regole (phase-out): non applicato, lo decide chi ha i permessi con la rotta /phaseout. */
  suggestedOutcome: { outcome: "phaseout"; reason: string; questionId: string } | null;
  /** Operazioni consentite all'utente corrente su questo audit (per mostrare i bottoni). */
  allowed: {
    edit: boolean;
    transitions: (typeof AUDIT_FLOW)[number][];
    answer: boolean; // checklist modificabile
    phaseout: boolean;
    sameScore: boolean;
    cap: boolean;
    upload: boolean;
    ncEdit: boolean;
    /** carica il programma e registra l'invio (AUD-F2.14) */
    programme: boolean;
  };
  /** Programma di audit (AUD-F2.14): previsto per tutti gli audit tranne i non annunciati. */
  programme: AuditProgrammeDto;
}
export interface AuditProgrammeDto {
  required: boolean;
  evidenceId: string | null;
  sentAt: string | null;
  sentTo: string | null;
  sentById: string | null;
}

/* ---------------- programma di audit (AUD-F2.14) ---------------- */
export const ProgrammeSetBody = z.object({ evidenceId: id.nullable() });
export const ProgrammeSentBody = z.object({ sentAt: isoDate.nullable(), sentTo: z.string().trim().max(500).default("") });
export const ProgrammeTemplateUploadBody = z.object({
  name: z.string().trim().min(1).max(200).regex(/\.docx$/i, "Serve un file Word .docx"),
  size: z.number().int().positive().max(20 * 1024 * 1024),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  note: z.string().max(500).optional().default(""),
});
export interface ProgrammeTemplateDto {
  id: string;
  v: number;
  name: string;
  size: number;
  sha256: string | null;
  active: boolean;
  note: string;
  uploadedById: string | null;
  createdAt: string;
}
export interface ProgrammeTemplateUploadDto {
  id: string;
  url: string;
  headers: Record<string, string>;
}

/* ---------------- non conformità ---------------- */
export const NcBody = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  severity: z.enum(NC_SEVERITIES),
  ownerId: id,
  questionId: id.nullable().optional(),
});
export const NcQuery = z.object({
  q: z.string().optional(),
  severity: z.enum(NC_SEVERITIES).optional(),
  status: z.enum(["open", "closed"]).optional(),
  supplierId: id.optional(),
  factoryId: id.optional(),
  auditId: id.optional(),
});
/** Il resto della riga del registro 2026 di Pier (AUD-F8.2). */
export interface NcSourceData {
  file: string;
  row: number;
  tematica?: string;
  tipo?: string;
  rilevataDa?: string;
  inData?: string;
  statoAvanzamento?: string;
  esito?: string;
  dataChiusuraEffettiva?: string;
  verificaEfficacia?: string;
  valutazioneEfficacia?: string;
  country?: string;
  categoria?: string;
  attivo?: string;
  fornitoreRegistro: string;
  abbinamento?: string;
}

export interface NcDto {
  id: string;
  code: string;
  /** null per le NC del registro 2026 che non coincidono con un audit del portale */
  auditId: string | null;
  auditCode: string | null;
  /** la NC sta sullo stabilimento (AUD-F8.4) */
  factoryId: string;
  factoryName: string;
  factoryCode: string | null;
  factoryCountry: string;
  source: "portal" | "registro-2026";
  sourceData: NcSourceData | null;
  supplierId: string;
  supplierName: string;
  questionId: string | null;
  questionCode: string | null; // "S 1.21"
  severity: (typeof NC_SEVERITIES)[number];
  title: string;
  description: string;
  ownerId: string;
  status: "open" | "closed";
  auto: boolean;
  followUp: "open" | "closed" | null;
  createdAt: string;
  closedAt: string | null;
  actionCount?: number;
  /** monitoraggio sospeso (fornitore o stabilimento chiuso o non attivo, AUD-F8.4): niente scadenze né escalation */
  monitoringOff: MonitoringOff | null;
}

/* ---------------- azioni correttive ---------------- */
export const ActionBody = z
  .object({
    title: z.string().min(1),
    description: z.string().optional().default(""),
    ownerId: id, // seguita da (Brandart)
    assigneeId: id.nullable().optional(), // utente fornitore
    assigneeName: z.string().optional(), // se il fornitore non ha utenze: "Nome - ruolo"
    priority: z.enum(PRIORITIES),
    openDate: isoDate,
    startDate: isoDate.nullable().optional(),
    dueDate: isoDate, // obbligatoria (decisione 19/10)
  })
  .refine((d) => !!d.assigneeId || !!d.assigneeName?.trim(), { message: "assignee required", path: ["assigneeId"] })
  .refine((d) => d.dueDate >= d.openDate, { message: "dueDate before openDate", path: ["dueDate"] });
export const ActionQuery = z.object({
  q: z.string().optional(),
  status: z.enum(ACT_FLOW).optional(),
  overdue: qBool.optional(),
  supplierId: id.optional(),
  factoryId: id.optional(),
  auditId: id.optional(),
  ncId: id.optional(),
});
export const ActionTransitionBody = z.object({ to: z.enum(ACT_FLOW), note: z.string().optional() });
/** Avanzamento: allegato obbligatorio (evidenceId di un'evidenza già confermata sull'azione). */
export const ActionProgressBody = z.object({
  progress: z.number().int().min(0).max(100),
  note: z.string().optional().default(""),
  evidenceId: id,
});
export const ActionRejectBody = z.object({ note: z.string().min(1) });
export interface ActionDto {
  id: string;
  code: string;
  ncId: string;
  ncCode: string;
  ncSeverity: (typeof NC_SEVERITIES)[number];
  auditId: string | null;
  auditCode: string | null;
  supplierId: string;
  supplierName: string;
  /** stabilimento della NC (AUD-F8.4) */
  factoryId: string;
  factoryName: string;
  title: string;
  description: string;
  ownerId: string;
  assigneeId: string | null;
  assigneeName: string; // nome dell'utente o testo libero
  priority: (typeof PRIORITIES)[number];
  openDate: string;
  startDate: string | null;
  dueDate: string;
  status: (typeof ACT_FLOW)[number];
  progress: number;
  /** scaduta e con il monitoraggio attivo: con il monitoraggio sospeso non è mai scaduta (AUD-F8.4) */
  overdue: boolean;
  monitoringOff: MonitoringOff | null;
  closedAt: string | null;
  validatedById: string | null;
  history: HistoryEntry[];
  createdAt: string;
}

/* ---------------- evidenze (S3) ---------------- */
export const EvidenceTarget = z.object({ type: z.enum(EVIDENCE_TARGETS), id: id });
/** Passo 1: chiede l'URL firmato. Con evidenceId carica una nuova versione di un'evidenza esistente. */
export const UploadUrlBody = z.object({
  supplierId: id,
  auditId: id.nullable().optional(),
  target: EvidenceTarget,
  name: z.string().min(1).max(200),
  size: z.number().int().positive(),
  contentType: z.string().min(1),
  description: z.string().optional().default(""),
  evidenceId: id.optional(),
  /** sha256 del file (hex). Il bucket delle evidenze ha Object Lock: S3 accetta il PUT solo con il checksum,
   *  firmato come header x-amz-checksum-sha256 (base64): il PUT va fatto con gli headers di UploadUrlDto, identici. */
  sha256: z.string().regex(/^[a-f0-9]{64}$/).optional(),
});
export interface UploadUrlDto {
  evidenceId: string;
  versionId: string;
  url: string; // PUT diretto su S3
  headers: Record<string, string>; // da rimandare identici nel PUT
  expiresAt: string;
}
export const EvidenceReviewBody = z.object({ status: z.enum(["approved", "rejected"]), note: z.string().optional() });
export const EvidenceQuery = z.object({
  supplierId: id.optional(),
  auditId: id.optional(),
  targetType: z.enum(EVIDENCE_TARGETS).optional(),
  targetId: id.optional(),
});
export interface EvidenceVersionDto {
  id: string;
  v: number;
  size: number;
  contentType: string;
  uploadedById: string | null;
  /** sha256 (hex) calcolato dal server alla conferma dell'upload; null per le versioni caricate prima dell'introduzione. */
  sha256: string | null;
  createdAt: string;
}
export interface EvidenceDto {
  id: string;
  supplierId: string;
  auditId: string | null;
  target: { type: (typeof EVIDENCE_TARGETS)[number]; id: string };
  name: string;
  description: string;
  status: (typeof EVIDENCE_STATUSES)[number] | null;
  reviewNote: string | null;
  reviewedById: string | null;
  reviewedAt: string | null;
  versions: EvidenceVersionDto[]; // solo versioni confermate, in ordine crescente
  createdAt: string;
  updatedAt: string;
}
/* Download: GET /evidence/:id/versions/:v/download -> 302 verso URL S3 firmato (registra il download nel log).
   Anteprima: stessa rotta con ?inline=1. */

/* ---------------- piano audit ---------------- */
export const PlanQuery = z.object({ year: z.coerce.number().int() });
export interface PlanCoverageRow {
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  risk: (typeof RISKS)[number];
  required: number;
  planned: number;
  missing: number;
  phaseOut: boolean;
}
export interface PlanDto {
  year: number;
  audits: AuditDto[];
  coverage: PlanCoverageRow[];
}
/** Import: POST /plan/import/preview (multipart, campo "file", .xlsx o .csv) -> PlanImportPreviewDto. */
export interface PlanImportRow {
  row: number; // numero riga nel file (1 = intestazione)
  raw: Record<string, string>;
  supplierId: string | null;
  supplierName: string | null;
  /** stabilimento (colonna facoltativa «Stabilimento»; senza, la sede del fornitore) */
  factoryId: string | null;
  factoryName: string | null;
  type: (typeof AUDIT_TYPES)[number];
  date: string | null;
  /** fine della finestra per i semi-annunciati (colonna facoltativa «Data fine», AUD-F8.4) */
  dateTo: string | null;
  auditorId: string | null;
  auditorName: string | null;
  why: string[]; // motivi di scarto; vuoto = riga valida
}
export interface PlanImportPreviewDto {
  rows: PlanImportRow[];
  valid: number;
  discarded: number;
}
export const PlanImportConfirmBody = z.object({
  rows: z.array(z.object({ supplierId: id, factoryId: id.nullable().optional(), type: z.enum(AUDIT_TYPES), date: isoDate, dateTo: isoDate.nullable().optional(), auditorId: id })).min(1),
  managerId: id.optional(),
});
/* Export: GET /plan/export.xlsx?year= ; modello: GET /plan/template.xlsx */

/* ---------------- dashboard ---------------- */
export const DashboardQuery = z.object({ year: z.coerce.number().int().optional(), supplierId: id.optional(), risk: z.enum(RISKS).optional() });
export interface DashboardDto {
  kpi: {
    suppliers: number;
    auditsPlanned: number; // planned + confirmed
    auditsInProgress: number; // in_progress + completed + in_validation
    auditsClosed: number;
    avgScore: number | null; // media degli score totali degli audit chiusi
    openNcs: number;
    /** NC aperte con almeno una AC scaduta (sottoinsieme delle aperte, AUD-F8.2) */
    overdueNcs: number;
    closedNcs: number;
    openNcsBySeverity: Record<(typeof NC_SEVERITIES)[number], number>;
    openActions: number;
    overdueActions: number;
    capPct: number | null; // % azioni done/closed sul totale
    phaseOutSuppliers: number;
    coveragePct: number | null; // visite pianificate / richieste nell'anno (cap 100 per fornitore)
  };
  ratingDistribution: Record<"A" | "B" | "C" | "D" | "NQ" | "-", number>;
  scoreTrend: { month: string; avg: number | null; count: number }[]; // ultimi 12 mesi, "YYYY-MM"
  upcomingAudits: AuditDto[]; // prossimi 30 giorni
  overdueActionsList: ActionDto[]; // max 10
  topRiskSuppliers: SupplierDto[]; // max 10, peggior rating / più NC aperte
}

/* ---------------- audit log, notifiche, config, ricerca ---------------- */
export const LogQuery = z.object({
  entity: z.string().optional(),
  entityId: id.optional(),
  supplierId: id.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  before: z.string().optional(), // cursore: ts ISO
});
export interface LogDto {
  id: string;
  ts: string;
  userId: string | null;
  userName: string;
  role: string;
  action: string; // create | update | delete | status | answer | login | download | view | import | export | notify | phaseout | cap | progress | reject | approve | seed
  entity: string;
  entityId: string | null;
  supplierId: string | null;
  detail: string;
  before: unknown;
  after: unknown;
  /** catena di hash del registro (sha256) */
  prevHash: string;
  hash: string;
}
export interface NotificationDto {
  id: string;
  key: string;
  kind: string;
  to: { id?: string; name: string; email: string }[];
  subject: string;
  body: string;
  refs: Record<string, string | null>;
  createdAt: string;
  sentAt: string | null; // legacy: lo stato di invio sta in MailOutboxDto
}

/* ---------------- posta in uscita (admin) ---------------- */
export const MAIL_STATUSES = ["queued", "sent", "failed", "skipped"] as const;
export type MailStatusValue = (typeof MAIL_STATUSES)[number];
export const MailOutboxQuery = z.object({
  status: z.enum(MAIL_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export interface MailOutboxDto {
  id: string;
  to: string;
  toName: string;
  cc: string[];
  subject: string;
  kind: string;
  status: MailStatusValue;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  providerMessageId: string | null;
  createdAt: string;
  /** testo semplice, assente per le mail con link a token (sensitive) */
  text: string | null;
}
export interface MailOutboxPageDto {
  items: MailOutboxDto[];
  total: number;
  provider: "log" | "graph" | "fake";
  /** all | tests (solo le prove «a me») | off */
  sendMode: "all" | "tests" | "off";
  allowedDomains: string[];
  counts: Record<MailStatusValue, number>;
}
export const ConfigBody = z.object({
  weights: z.object({ S: z.number().min(0), H: z.number().min(0), E: z.number().min(0) }),
  visits: z.object({ high: z.number().int().min(0), medium: z.number().int().min(0), low: z.number().int().min(0) }),
  functionHead: z.object({ name: z.string(), email: z.string() }),
  managerEsc: z.boolean(),
  /** Regole del rating (admin → Configurazione). Se assenti restano quelle attuali. */
  zeroRule: z.object({ onNcCritical: z.enum(ZERO_ACTIONS), onZeroTolerance: z.enum(ZERO_ACTIONS) }).optional(),
  supplierRating: z.enum(SUPPLIER_RATING_RULES).optional(),
  auditCodeYear: z.enum(CODE_YEAR_MODES).optional(),
  showRisk: z.boolean().optional(),
  vendorhubNewInactive: z.boolean().optional(),
  retention: z
    .object({
      evidenceYears: z.number().int().min(1).max(100),
      auditLogYears: z.number().int().min(1).max(100),
      closedAuditYears: z.number().int().min(1).max(100),
      inactiveSupplierUserMonths: z.number().int().min(1).max(240),
    })
    .optional(),
});
export type ConfigDto = AppConfig;

/* ---------------- configurazione notifiche (admin) ---------------- */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");
const tzName = z.string().min(1).refine((v) => {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: v });
    return true;
  } catch {
    return false;
  }
}, "Fuso orario non valido");
export const NotificationEventConfigBody = z.object({
  enabled: z.boolean(),
  roles: z.array(z.enum(RECIPIENT_ROLES)).max(RECIPIENT_ROLES.length),
  extra: z.array(z.string().trim().toLowerCase().email("Indirizzo email non valido")).max(20),
  /** solo «mail» è attivo: Teams è predisposto, in arrivo */
  channels: z.array(z.enum(ACTIVE_CHANNELS as unknown as ["mail"])).min(1, "Almeno il canale mail").max(1).default(["mail"]),
});
export const NotificationsConfigBody = z.object({
  enabled: z.boolean(),
  events: z.object(Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, NotificationEventConfigBody])) as Record<NotificationKind, typeof NotificationEventConfigBody>),
  reminders: z.object({
    leadDays: z.array(z.number().int().min(0).max(365)).min(1).max(12),
    overdueEveryDays: z.number().int().min(1).max(90),
    managerAfterDays: z.number().int().min(1).max(365),
    functionHeadAfterDays: z.number().int().min(1).max(365),
  }),
  window: z.object({ hoursEnabled: z.boolean(), from: hhmm, to: hhmm, timezone: tzName, workdaysOnly: z.boolean() }),
});
export const NotificationTestBody = z.object({ kind: z.enum(NOTIFICATION_KINDS as [NotificationKind, ...NotificationKind[]]) });
export const NotificationPreviewQuery = z.object({ kind: z.enum(NOTIFICATION_KINDS as [NotificationKind, ...NotificationKind[]]), lang: z.enum(["it", "en"]).default("it") });
export interface NotificationPreviewDto {
  kind: NotificationKind;
  lang: "it" | "en";
  subject: string;
  text: string;
}
export interface NotificationsConfigDto {
  config: NotificationsConfig;
  functionHead: { name: string; email: string };
  provider: "log" | "graph" | "fake";
  /** mail in coda fuori finestra di invio, in attesa dell'apertura */
  windowOpen: boolean;
  queued: number;
}
export interface SearchResultDto {
  suppliers: { id: string; code: string; name: string }[];
  audits: { id: string; code: string; supplierName: string; status: string }[];
  ncs: { id: string; code: string; title: string }[];
  actions: { id: string; code: string; title: string }[];
}

/* ---------------- supplier detail ---------------- */
export interface SupplierDetailDto extends SupplierDto {
  audits: AuditDto[];
  ncs: NcDto[];
  actions: ActionDto[];
  evidence: EvidenceDto[];
  contacts: UserDto[]; // utenti con ruolo supplier collegati
  factories: FactoryDto[];
  /** contatti anagrafici (VendorHub) del fornitore e dei suoi stabilimenti: non sono utenti */
  vendorContacts: ContactDto[];
  scoreHistory: { auditId: string; code: string; date: string; total: number | null }[];
}

/**
 * Mappa delle rotte (tutte sotto /api/v1, JSON salvo dove indicato).
 *
 * auth        POST /auth/login · POST /auth/logout · GET /auth/me · POST /auth/accept-invite · POST /auth/change-password
 * users       GET /users · POST /users/invite · PATCH /users/:id · POST /users/:id/reinvite            (admin)
 * people      GET /people  -> PersonRef & {role, supplierId}[] (tutti gli utenti autenticati, per select e nomi)
 * suppliers   GET /suppliers · GET /suppliers/:id · POST /suppliers · PATCH /suppliers/:id
 *             GET /suppliers/:id/dossier.xlsx · GET /suppliers/:id/dossier.pdf
 * checklist   GET /checklists · GET /checklists/:versionId/questions · POST /checklists/:versionId/questions
 *             PATCH /questions/:id   (409 se la versione è usata da audit chiusi: si crea una nuova versione)
 *             POST /checklists/:versionId/clone {code,title} -> ChecklistVersionDto
 * audits      GET /audits · GET /audits/:id · POST /audits · PATCH /audits/:id · POST /audits/:id/transition
 *             PUT /audits/:id/answers/:questionId · PUT /audits/:id/extra/:questionId
 *             POST /audits/:id/observations · DELETE /audits/:id/observations/:obsId
 *             POST /audits/:id/phaseout · POST /audits/:id/same-score · POST /audits/:id/cap
 *             POST /audits/:id/ncs (NC manuale) · GET /audits/:id/report.pdf
 * ncs         GET /ncs · GET /ncs/:id · PATCH /ncs/:id · POST /ncs/:id/actions
 * actions     GET /actions · GET /actions/:id · PATCH /actions/:id · POST /actions/:id/transition
 *             POST /actions/:id/progress · POST /actions/:id/reject
 * evidence    GET /evidence · POST /evidence/upload-url · POST /evidence/:id/versions/:versionId/confirm
 *             GET /evidence/:id/versions/:v/download · POST /evidence/:id/review · DELETE /evidence/:id
 * plan        GET /plan?year · POST /plan/import/preview (multipart) · POST /plan/import/confirm
 *             GET /plan/export.xlsx?year · GET /plan/template.xlsx
 * dashboard   GET /dashboard
 * exports     GET /exports/suppliers.xlsx · /exports/audits.xlsx · /exports/ncs.xlsx · /exports/actions.xlsx
 *             /exports/log.xlsx · /exports/dataset.json (admin)
 * log         GET /log                    (admin, manager; il fornitore vede solo le righe del proprio fornitore)
 * notif.      GET /notifications (admin) · POST /scheduler/run (admin) · GET|PUT /notifications/config · POST /notifications/test · GET /notifications/preview
 * config      GET /config · PUT /config (admin)
 * search      GET /search?q
 * admin       POST /admin/seed-demo · POST /admin/seed-reset   (admin, solo con ALLOW_DEMO_SEED=true)
 * health      GET /healthz (fuori da /api/v1, nessuna auth)
 * docs        GET /api/docs (OpenAPI generata)
 */
export const API_PREFIX = "/api/v1";

/* ---------------- API key (admin) ---------------- */
export const ApiKeyCreateBody = z.object({
  name: z.string().min(1).max(80),
  role: z.enum(ROLES),
  supplierId: id.nullable().optional(), // obbligatorio se role = supplier
  expiresInDays: z.number().int().min(1).max(730).nullable().optional(), // null = senza scadenza
});
export interface ApiKeyDto {
  id: string;
  name: string;
  prefix: string;
  role: (typeof ROLES)[number];
  supplierId: string | null;
  createdById: string | null;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}
/** Risposta alla creazione: il token completo compare solo qui. Uso: Authorization: Bearer <token>. */
export interface ApiKeyCreatedDto {
  key: ApiKeyDto;
  token: string;
}
/* api-keys  GET /api-keys · POST /api-keys · DELETE /api-keys/:id (revoca)   (admin) */

/* ---------------- MCP con login della persona (AUD-F2.7) ---------------- */
/** Richiesta di collegamento di un client MCP, mostrata nella pagina di consenso (GET /oauth/mcp/request). */
export interface McpAuthRequestDto {
  clientName: string;
  /** host verso cui torna il browser dopo il consenso (es. claude.ai, localhost) */
  redirectHost: string;
  expiresAt: string;
  /** motivo per cui l'utente corrente non puo' dare il consenso (fornitore, accesso non SSO), altrimenti null */
  blocked: string | null;
}
export const McpDecisionBody = z.object({ req: z.string().min(10).max(200) });
export interface McpRedirectDto {
  redirectUrl: string;
}
/** Sessione MCP attiva (persona + client): GET /mcp-sessions?scope=mine|all · DELETE /mcp-sessions/:id (revoca). */
export interface McpSessionDto {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  clientName: string;
  sso: boolean;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
}

/* ---------------- informative GDPR (AUD-F3.13) ---------------- */
export const POLICY_AUDIENCES = ["supplier", "internal"] as const;
export type PolicyAudience = (typeof POLICY_AUDIENCES)[number];
export const PolicyCreateBody = z.object({
  code: z.string().min(1).max(80),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(100_000),
  audience: z.enum(POLICY_AUDIENCES),
});
export const PolicyPatchBody = PolicyCreateBody.partial();
export const PolicyAcceptBody = z.object({ accepted: z.literal(true) });
export const PolicyAcceptancesQuery = z.object({ policyVersionId: id.optional(), limit: z.coerce.number().int().min(1).max(2000).default(500) });
export interface PolicyVersionDto {
  id: string;
  code: string;
  title: string;
  body: string;
  audience: PolicyAudience;
  active: boolean;
  publishedAt: string | null;
  createdAt: string;
  acceptances?: number;
}
export interface PolicyAcceptanceDto {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  supplierName: string | null;
  policyVersionId: string;
  policyCode: string;
  policyTitle: string;
  acceptedAt: string;
  ip: string | null;
  userAgent: string | null;
}
/* policies  GET /policies/pending · POST /policies/:id/accept   (utente corrente; fuori dal blocco "policy_acceptance_required")
             GET /policies · POST /policies · PATCH /policies/:id · DELETE /policies/:id (bozza) · POST /policies/:id/publish
             GET /policies/acceptances · GET /policies/acceptances.xlsx   (admin) */

/* ---------------- conservazione dati ---------------- */
export interface RetentionItemDto {
  kind: "evidence" | "auditLog" | "closedAudit";
  ref: string;
  label: string;
  date: string;
  supplierId?: string | null;
}
export interface RetentionRunDto {
  id: string;
  ranAt: string;
  trigger: string;
  summary: { evidenceVersions: number; auditLogRows: number; closedAudits: number; disabledUsers: number };
  items: RetentionItemDto[];
  /** elenco troncato a un massimo di righe per tipo; i totali sono in summary */
  truncated: boolean;
  disabled: { id: string; name: string; email: string; supplierId: string | null; lastSeen: string }[];
  config: AppConfig["retention"];
}
/* retention  GET /retention/report (ultima esecuzione) · POST /retention/run   (admin) */

/* ---------------- integrità ---------------- */
export const IntegrityVerifyBody = z.object({
  sample: z.number().int().min(0).max(500).optional(),
  /** "start" (default) = ricalcola tutta la catena; "checkpoint" = dall'ultima verifica riuscita */
  since: z.enum(["start", "checkpoint"]).optional(),
});
export interface IntegrityMismatchDto {
  evidenceId: string;
  versionId: string;
  name: string;
  v: number;
  expected: string;
  actual: string | null;
  reason: "hash_mismatch" | "object_missing";
}
export interface IntegrityCheckDto {
  id: string;
  ranAt: string;
  userName: string;
  ok: boolean;
  scope: "start" | "checkpoint";
  chainRows: number;
  fromId: string;
  lastId: string | null;
  lastHash: string;
  firstBrokenId: string | null;
  sampled: number;
  mismatches: IntegrityMismatchDto[];
  /** versioni di evidenza senza sha256 (caricate prima dell'introduzione): non verificabili */
  unhashed: number;
}
/* integrity  POST /integrity/verify {sample?, since?} · GET /integrity/checks   (admin) */
